import React, { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { observer } from 'mobx-react';
import { Container } from 'reactstrap';
import { Helmet } from 'react-helmet';
import { TextDefault, TextLead, ThemedButton } from '@awarego-ui/components';
import { Alert, Box, TextField, Typography } from '@mui/material';
import { useStores } from '../../hooks/use-stores';
import services from '../../services';
import { extractErrorMessage } from '../../utils/helpers';

/**
 * After sign-in redirect, users with company access can add themselves to a
 * **hidden_on_demand** list (not shown on the main Employee lists tab).
 */
function JoinOnDemandList({ companyId }) {
    const { listId } = useParams();
    const { commonStore } = useStores();
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(false);
    const [error, setError] = useState(null);

    const shareUrl = useMemo(() => {
        const base =
            typeof window !== 'undefined' && window.location
                ? window.location.origin
                : '';
        return `${base}/users/join-on-demand-list/${encodeURIComponent(listId)}`;
    }, [listId]);

    const enroll = async () => {
        setBusy(true);
        setError(null);
        try {
            await services.Companies.listsService(companyId).onDemandSelfEnroll(
                listId
            );
            setDone(true);
            commonStore.showMessage(
                'You were added to this audience list.',
                'success'
            );
        } catch (e) {
            setError(extractErrorMessage(e));
        } finally {
            setBusy(false);
        }
    };

    const copyShareLink = async () => {
        try {
            await navigator.clipboard.writeText(shareUrl);
            commonStore.showMessage('Link copied to clipboard.', 'success');
        } catch {
            commonStore.showMessage('Could not copy link.', 'error');
        }
    };

    return (
        <Container className="manager-content">
            <Helmet>
                <title>Join audience list</title>
            </Helmet>
            <Box sx={{ maxWidth: 640, py: 2 }}>
                <TextLead bold>On-demand audience list</TextLead>
                <TextDefault style={{ marginTop: 12 }}>
                    Use this page after you sign in (for example from a training
                    redirect). Managers can share the link below so others with
                    access to this company can join the same hidden audience list.
                </TextDefault>
                {error && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                        {error}
                    </Alert>
                )}
                {done && (
                    <Alert severity="success" sx={{ mt: 2 }}>
                        You are on this list. You can close this tab.
                    </Alert>
                )}
                <Box sx={{ mt: 3, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <ThemedButton
                        mode="primary"
                        text={busy ? 'Joining…' : 'Add me to this list'}
                        disabled={busy || done}
                        onClick={enroll}
                    />
                </Box>
                <Typography variant="subtitle2" sx={{ mt: 4, mb: 1 }}>
                    Shareable join link
                </Typography>
                <TextField
                    fullWidth
                    size="small"
                    value={shareUrl}
                    InputProps={{ readOnly: true }}
                />
                <ThemedButton
                    mode="secondary"
                    text="Copy link"
                    style={{ marginTop: 12 }}
                    onClick={copyShareLink}
                />
            </Box>
        </Container>
    );
}

export default observer(JoinOnDemandList);
